import axios from "axios";
import { toast } from "sonner";

export const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API_BASE = `${BACKEND_URL}/api`;

const api = axios.create({
    baseURL: API_BASE,
    withCredentials: true,
});

// Attach Authorization header from localStorage as fallback
api.interceptors.request.use((config) => {
    const token = localStorage.getItem("rd_token");
    if (token) config.headers.Authorization = `Bearer ${token}`;
    return config;
});

// Detect an expired / invalid session (401) and boot the user to the login
// page instead of letting drivers silently accumulate a queue of "saved
// offline" changes that never actually sync.
let redirectingToLogin = false;
api.interceptors.response.use(
    (r) => r,
    (err) => {
        const status = err?.response?.status;
        const reqUrl = err?.config?.url || "";
        const hadToken = !!err?.config?.headers?.Authorization;
        // Portal session revoked: quietly drop back to a fresh sign-in screen.
        if (status === 401 && hadToken && window.location.pathname.startsWith("/portal")
            && !/\/portal\/(login|register)$/.test(reqUrl)) {
            localStorage.removeItem("rd_token");
            localStorage.removeItem("rd_portal_email");
            window.location.replace("/portal/login");
            return new Promise(() => {});
        }
        if (status === 401 && !redirectingToLogin) {
            const path = window.location.pathname;
            const safePages = ["/login", "/portal/login", "/agreement/sign",
                                "/signup", "/embed/signup", "/embed-signup",
                                "/pay/"];
            const onSafePage = safePages.some((p) => path.startsWith(p));
            if (!onSafePage) {
                redirectingToLogin = true;
                localStorage.removeItem("rd_token");
                toast.error("Your session expired — please sign in again",
                            { duration: 6000 });
                setTimeout(() => {
                    // Preserve the current URL so we come back after login
                    const next = encodeURIComponent(
                        window.location.pathname + window.location.search);
                    window.location.replace(`/login?next=${next}`);
                }, 900);
            }
        }
        // Flag the error so callers can distinguish auth from network.
        if (err?.response) err.isAuthError = status === 401 || status === 403;
        return Promise.reject(err);
    },
);

export function formatApiErrorDetail(detail) {
    if (detail == null) return "Something went wrong. Please try again.";
    if (typeof detail === "string") return detail;
    if (Array.isArray(detail)) {
        return detail
            .map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e)))
            .filter(Boolean)
            .join(" ");
    }
    if (detail && typeof detail.msg === "string") return detail.msg;
    return String(detail);
}

export default api;
